import type { BetslipProps } from "@/features/fu-widgets/library-types";
import type { TenantSettings } from "@/lib/tenant/TenantSettingsProvider";
import { mapTenantToFUWidgetTheme, resolveThemeMode } from "@/features/theme/fu-widget-theme";

/**
 * Build the library `<Betslip />` props from tenant settings.
 * Excludes `sdk`, `language`, and `labels` — those are supplied by the mount wrapper.
 *
 * `operatorPrimaryColor` overrides the library's bookmaker-color auto-detect
 * only when the tenant explicitly sets it; otherwise it is omitted so the
 * library adopts the bookmaker's brand color from the odds response.
 */
export function mapTenantToBetslipProps(
    settings: TenantSettings
): Omit<BetslipProps, "sdk" | "language" | "labels"> {
    const betslip = settings.betslip;
    if (!betslip) {
        // Should never reach here — CanvasBetslip gates on this upstream.
        // Fall back to the library's own defaults by returning a near-empty object.
        return { position: "bottom-right" };
    }

    // Betslip works with a SINGLE operator. Default to the first entry of the
    // tenant's shared odds operator list (features.odds.operators) so the
    // widget and the page's odds display always show the same bookmaker.
    // tenant.betslip.operatorId acts as an explicit override when set.
    const operatorId = betslip.operatorId || settings.odds.operators[0];

    // Prefer the per-operator affiliate override from the odds config, then
    // the betslip-level override, then the shared default affiliate.
    const affiliateId =
        (operatorId ? settings.odds.affiliateIds[operatorId] : undefined) ??
        betslip.affiliateId ??
        settings.odds.affiliateId ??
        null;

    // Match the tenant's own site theme — Betslip floats over the page, so a
    // light-background tenant gets a light widget, a dark-background tenant a
    // dark one. The shared helper hardcodes `mode: "dark"` for gamification
    // tuning; we override here, same pattern as EventCardWrapper.
    const themeMode = resolveThemeMode(settings);
    const baseTheme = { ...mapTenantToFUWidgetTheme(settings), mode: themeMode };

    // Apply the operator primary color override on top of the base theme,
    // only when the tenant has set it. When unset, the library's bookmaker
    // auto-detect kicks in and paints the accent from the odds response.
    const themeOptions = betslip.operatorPrimaryColor
        ? {
              ...baseTheme,
              colorSchemes: {
                  ...baseTheme.colorSchemes,
                  [themeMode]: {
                      ...baseTheme.colorSchemes?.[themeMode],
                      palette: {
                          ...baseTheme.colorSchemes?.[themeMode]?.palette,
                          primary: {
                              ...baseTheme.colorSchemes?.[themeMode]?.palette?.primary,
                              plainColor: betslip.operatorPrimaryColor,
                          },
                      },
                  },
              },
          }
        : baseTheme;

    return {
        position: betslip.position,
        maxSelections: betslip.maxSelections,
        stakePresets: betslip.stakePresets,
        oddsPollingInterval: (betslip.oddsPollingInterval ?? 30) * 1000,
        currency: betslip.currency,
        operatorId,
        ...(betslip.ctaUrlTemplate ? { ctaUrlTemplate: betslip.ctaUrlTemplate } : {}),
        ...(betslip.brandingLogoUrl ? { brandingLogoUrl: betslip.brandingLogoUrl } : {}),
        ...(affiliateId ? { affiliateId } : {}),
        themeOptions,
    };
}
